// packages/engine/src/process-trace-enumerator.ts
//
// The process-test trace-set evaluator core (PROCESS.md §14; impl plan P1a+P1c).
// Materialises the pin-constrained trace(s) from a process's IR:
//
//   1. buildChoiceBridge — walk the IR (via the SHARED render emitter, so the
//      nodeIds are authoritative, never hand-mirrored) and map each choice
//      point's stable KEY (an `if`/`else if` condition, a `try`/`catch` label, a
//      `select` case, a named `parallel` branch, an `await` head, an `as` step
//      alias) to the emitter's positional container/step nodeId. This is the
//      choice-key ↔ nodeId bridge the `when` pins resolve THROUGH.
//
//   2. enumerateTraces — resolve each `when` pin against the bridge, fold the
//      if/try selections into a `branchSelection` map, and drive `walkProcess`
//      (the SAME emitter the render walker uses) to produce the trace. Because
//      the enumerator reuses `walkProcess` rather than re-traversing, its trace
//      is byte-equal to the render walker's under the equivalent selection —
//      the sync-equality anchor. A loop-free / `go`-free trace is returned
//      byte-identical; loops and `go` are then unrolled (step 3).
//
//   3. P1c bounding — plain `each` loops (nodeId `loop:N`) and backward `go`
//      re-entries are unrolled into the trace: a numeric head or a `when` count
//      pin gives the exact count; an unbounded collection / `go` is bounded to
//      `maxLoop` (default 3) then declared OPEN, reusing the `process-latency.ts`
//      `Reason` vocabulary (`unbounded-each` / `go`). The trace-set is capped;
//      overflow is OPEN("cap") + a warning, never a silent truncation. Retry
//      (`retry:N`) is NOT a P1c loop — its outcome selection is P1b's.
//
// P1a+P1c consume if/try + loops/`go`; select/parallel/retry/reversible/await
// RESOLUTION is P1b. The bridge already records every choice kind (load-bearing
// across P1b/P6/P8); the enumerator surfaces the not-yet-consumed points as
// `open` so downstream phases pick them up with no bridge change.

import type { Diagnostic } from "./types.js";
import type { DiagnosticCode } from "./diagnostics/codes.js";
import type {
  ProcessModel,
  SubprocessModel,
  ParallelNode,
  SelectNode,
  BranchNode,
  BranchArm,
  ReentryNode,
  AwaitNode,
  CallNode,
  LoopNode,
  LoopHead,
  SpliceNode,
  Flow,
  ProcessNode,
} from "./process-model.js";
import type {
  NodeId,
  WalkerContext,
  WalkNode,
  WalkStep,
} from "./process-walker-types.js";
import type { Reason, LatencyEnv, Injected } from "./process-latency.js";
import { buildLatencyEnv } from "./process-latency-env.js";
import type { ResolvedModel } from "./resolver.js";
import type { TestPin, TestPinValue, TestRef } from "./v010-ast.js";
import { emitSteps, type EmitContainer, type EmitGroup } from "./process-step-emitter.js";
import { walkProcess } from "./process-walker.js";
import { produceTrace, type Trace, type TraceEvent, type ParallelOutcome } from "./process-trace-producer.js";
import { buildInjectionMap, buildFailureMap, isFailurePin, resolveRaces, resolveCompletions } from "./process-trace-timing.js";

/** The choice-point kinds the bridge records. `if`/`try` are consumed by the
 *  enumerator core (P1a); `loop` drives the P1c count bound; the rest are
 *  recorded for later phases. */
export type ChoiceKind = "if" | "try" | "select" | "parallel" | "await" | "as" | "loop" | "retry" | "goloop";

/** One resolved choice point: its stable key, kind, and the emitter nodeId the
 *  pin drives. `containerNodeId` + `armLabel` are the `branchSelection` inputs
 *  for if/try; `stepNodeId` anchors `await`/`as` step references. */
export interface ChoicePoint {
  /** The namespaced lookup key: `<scope>.<raw>` when inside a labelled `do`
   *  splice (design §6), else the bare `raw`. This is what `byKey` indexes and
   *  a `when` pin resolves through. */
  readonly key: string;
  /** The un-namespaced key (a condition string / catch label / `as` alias) —
   *  the form the producer's own machinery (go-loop alias, arm label) consumes. */
  readonly raw: string;
  /** The enclosing `do`-splice label path (`pay1`, `out.innr`), or "" at root. */
  readonly scope: string;
  readonly kind: ChoiceKind;
  /** The selectable container (`p:X/branch:0`, `p:X/guard:0`, …). */
  readonly containerNodeId?: string;
  /** The arm label to write into `branchSelection[containerNodeId]`. */
  readonly armLabel?: string;
  /** The arm's own group nodeId. */
  readonly groupNodeId?: string;
  /** For `await`/`as` — the step nodeId the reference resolves at. */
  readonly stepNodeId?: string;
  /** A point recorded for COVERAGE only, not pin resolution — kept out of `byKey`
   *  so it never collides with a real pin key. A label-less loop (`each in items`)
   *  has no name to pin, but still contributes its 0/1/many coverage cells. */
  readonly unaddressable?: boolean;
}

/** The choice-key ↔ nodeId table. */
export interface ChoiceBridge {
  readonly points: readonly ChoicePoint[];
  /** key → the choice point(s) that key names (≥2 ⇒ ambiguous). */
  readonly byKey: ReadonlyMap<string, readonly ChoicePoint[]>;
  /** selectable containerNodeId → its arm choice points. */
  readonly byContainer: ReadonlyMap<string, readonly ChoicePoint[]>;
  /** `do`-splice label path (`pay1`, `out.innr`) → the splice container nodeId,
   *  so an assertion `pay1.Payment.charge` scopes to the steps inlined under it
   *  (design §6). Nested `do`s carry the full dotted path. */
  readonly byLabel: ReadonlyMap<string, string>;
  /** Every emitted call/await/note IR node — the injection map (P4) matches
   *  `after`/`takes` pins against these. */
  readonly actionNodes: readonly ProcessNode[];
  /** Each `parallel` container's producer nodeId → its IR node, for the P4 race
   *  resolver (`resolveRaces`) and the P5 per-branch completion resolver. */
  readonly parallels: ReadonlyMap<string, ParallelNode>;
  /** Each `select` container's producer nodeId → its IR node, so the P5 `list`
   *  pin resolver maps case-label items → case indices + clamps by the count. */
  readonly selects: ReadonlyMap<string, SelectNode>;
  /** Each `if` container's producer nodeId → its IR node, so the P5 go-sequence
   *  pin resolver can walk the gated arm for the `go` it controls. */
  readonly branches: ReadonlyMap<string, BranchNode>;
  /** Each emitted action node → the enclosing `guard`/`retry` container nodeIds
   *  (innermost last). P3 uses it to pin a guard whose attempt carries an injected
   *  failure to `try` (the injection fixes the outcome — don't also enumerate its
   *  catch arm as a separate trace). */
  readonly guardOfNode: ReadonlyMap<ProcessNode, readonly string[]>;
}

/** A choice point NOT expanded into the P1b trace-set — a `select`/`parallel`
 *  point whose outcome derivation is deferred (run-set from an ordered-list pin
 *  = P5, completion from virtual-clock timings = P4). It is produced at its
 *  structural default; downstream phases pick it up. (Unpinned `if`/`try`
 *  containers are NOT open — they ARE enumerated below.) */
export interface OpenChoice {
  readonly containerNodeId: string;
  readonly kind: ChoiceKind;
  readonly armLabels: readonly string[];
}

/** Why a bound was declared OPEN (P1c). Reuses the `process-latency.ts` fold
 *  vocabulary for loops/`go` (`unbounded-each` / `go`) so a test and a latency
 *  estimate speak the same "why is this unbounded" language; `"cap"` is the
 *  enumeration-specific overflow reason (not a latency concept, kept local). */
export type OpenBoundReason = Reason | "cap";

/** One OPEN bound emitted while materialising the trace-set (P1c). A loop or
 *  `go` unrolled to `boundedTo` iterations then declared OPEN; or the trace-set
 *  overflowed the cap (`reason: "cap"`, no `nodeId`). */
export interface OpenBound {
  readonly reason: OpenBoundReason;
  /** The loop / `go` container nodeId the OPEN came from (absent for `"cap"`). */
  readonly nodeId?: string;
  /** The `maxLoop` bound applied before declaring OPEN (absent for `"cap"`). */
  readonly boundedTo?: number;
}

/** Bounding knobs (P1c). `maxLoop` bounds an unpinned unbounded `each` / `go`
 *  before OPEN; `traceCap` bounds the trace-set cardinality before OPEN("cap").
 *  Both are surfaced to the runner (`--maxLoop`, per-test `maxLoop:`) in P7. */
export interface EnumerateOptions {
  readonly maxLoop?: number;
  readonly traceCap?: number;
  /** The resolved model (P4) — builds the `LatencyEnv` the race resolver + time
   *  budgets fold against (declared `latency` fields). Absent ⇒ a trivial env
   *  (every interface missing-latency), so only pin-injected timing resolves. */
  readonly model?: ResolvedModel;
}

/** Default unbounded-loop / `go` bound (design §12 open-question 1). */
export const DEFAULT_MAX_LOOP = 3;
/** Default trace-set cardinality cap before OPEN("cap"). */
export const DEFAULT_TRACE_CAP = 256;

export interface EnumerateResult {
  /** The pin-constrained trace-SET: one {@link Trace} per surviving cartesian
   *  combination of the unpinned `if`/`try` arms (P1b, deduplicated — an
   *  unreachable nested choice collapses to the same trace), each with its loops
   *  and backward `go`s unrolled + bounded (P1c). Fully pinned + loop-free ⇒
   *  exactly one. */
  readonly traces: readonly Trace[];
  readonly open: readonly OpenChoice[];
  /** OPEN bounds emitted while bounding loops/`go`/the trace-set (P1c). */
  readonly openBounds: readonly OpenBound[];
  /** `do`-splice label path → its container nodeId (design §6) — lets the
   *  assertion evaluator scope a `pay1.Payment.charge` ref to the steps inlined
   *  under that splice. */
  readonly spliceLabels: ReadonlyMap<string, string>;
  readonly diagnostics: readonly Diagnostic[];
  /** The `LatencyEnv` + pin-injection map the resolver used (P4) — reused by the
   *  time-budget evaluator so it folds the pruned trace under the same pins. */
  readonly env: LatencyEnv;
  readonly inject: ReadonlyMap<ProcessNode, Injected>;
  /** P5 §11 — the choice-space CELL ids this test's pin-constrained trace-set
   *  exercised (an if/try/select/parallel arm group nodeId reached by a step, a
   *  loop-count bucket `<loop>#0|1|many`, or a `<parallel>#merge-fail`). The
   *  coverage pass unions these across a process's tests against
   *  {@link processChoiceSpace}. Computed here because the resolved loop counts /
   *  outcomes aren't recoverable from the deduped linear traces alone. */
  readonly coverageCells: ReadonlySet<string>;
}

/** The loop-count coverage bucket (design §11: 0 / 1 / many). */
export function loopBucket(count: number): "0" | "1" | "many" {
  return count === 0 ? "0" : count === 1 ? "1" : "many";
}

/** The rootId a `walkProcess` emits under (process-walker.ts) — the bridge must
 *  derive nodeIds through the SAME prefix or the tables won't align. */
function rootIdFor(process: ProcessModel): string {
  return `p:${process.id ?? process.name}`;
}

/**
 * Build the choice-key ↔ nodeId table by subscribing to the shared emitter at
 * `showAllBranches: true` (so every arm — selected or not — surfaces its
 * container/group nodeIds). Reads keys from the IR nodes, never the emitter's
 * positional labels (a named `parallel` branch or an `await` head is not in the
 * emitted label). Pure — no diagnostics; ambiguity is reported at resolution.
 */
export function buildChoiceBridge(process: ProcessModel, ctx: WalkerContext): ChoiceBridge {
  const points: ChoicePoint[] = [];
  // Container stack + per-container branch counter (the emitter labels parallel
  // branches positionally; we count them to index into the IR for the name).
  const stack: { container: EmitContainer; branchIndex: number }[] = [];
  // The `do`-splice label path in scope (`["pay1"]`, `["out","innr"]`) — pushed
  // on entry to a LABELLED splice, popped on close. An unlabelled `do`
  // contributes no segment (its inner keys stay bare — ambiguous across two
  // sites → a resolution diagnostic, never silent; design §6).
  const spliceLabels: string[] = [];
  const byLabel = new Map<string, string>();
  const actionNodes: ProcessNode[] = [];
  const parallels = new Map<string, ParallelNode>();
  const selects = new Map<string, SelectNode>();
  const branches = new Map<string, BranchNode>();
  const guardOfNode = new Map<ProcessNode, string[]>();
  // `as` aliases seen so far (alias → its step nodeId) + go-loop targets already
  // recorded — a backward `go` whose target `as` is a real EXECUTION predecessor
  // (not in a mutually-exclusive sibling arm) is a loop boundary (item 6 gap 1).
  const seenAs = new Map<string, string>();
  const goloopSeen = new Set<string>();
  // The group nodeIds of `parallel await` timer branches. The desugared `await
  // <head>` STEP inside such a branch is a walk artifact — its choice point would
  // DUPLICATE the boundary point below (same key), making the pin ambiguous — so
  // its registration is suppressed; the boundary point is the one keyable place.
  const awaitTimerGroups: string[] = [];

  const scope = (): string => spliceLabels.join(".");
  const nsKey = (raw: string): string => {
    const s = scope();
    return s ? `${s}.${raw}` : raw;
  };

  const add = (raw: string, kind: ChoiceKind, extra: Omit<ChoicePoint, "key" | "raw" | "scope" | "kind">): void => {
    points.push({ key: nsKey(raw), raw, scope: scope(), kind, ...extra });
  };

  emitSteps(
    process.body,
    {
      subModels: ctx.processByName
        ? new Map<string, SubprocessModel | ProcessModel>([...ctx.processByName, ...ctx.subprocessByName])
        : ctx.subprocessByName,
      showAllBranches: true,
      rootId: rootIdFor(process),
    },
    {
      step(u) {
        actionNodes.push(u.node);
        const guards = stack.filter((s) => s.container.irKind === "guard" || s.container.irKind === "retry").map((s) => s.container.nodeId);
        if (guards.length) guardOfNode.set(u.node, guards);
        if (u.kind === "await") {
          // Skip the desugared `parallel await` timer step — the boundary point keys it.
          if (!awaitTimerGroups.some((pre) => u.nodeId.startsWith(`${pre}/`))) {
            add((u.node as AwaitNode).target.text, "await", { stepNodeId: u.nodeId });
          }
        } else if (u.kind === "call" && u.isLastHop) {
          const an = (u.node as CallNode).asName?.name;
          if (an) { add(an, "as", { stepNodeId: u.nodeId }); seenAs.set(an, u.nodeId); }
        } else if (u.kind === "go") {
          // A BACKWARD `go` is a loop only if its `as` target is a real execution
          // predecessor — NOT in a mutually-exclusive sibling arm (`if { as foo }
          // else { go foo }` is a jump, not a loop; the unroller never loops it).
          const t = (u.node as ReentryNode).target.name?.trim();
          const asId = t ? seenAs.get(t) : undefined;
          if (t && asId && !goloopSeen.has(t) && !mutuallyExclusive(asId, u.nodeId)) {
            goloopSeen.add(t);
            add(t, "goloop", { containerNodeId: `goloop:${t}`, unaddressable: true });
          }
        }
      },
      open(c) {
        if (c.irKind === "parallel") parallels.set(c.nodeId, c.node as ParallelNode);
        if (c.irKind === "branch") branches.set(c.nodeId, c.node as BranchNode);
        // A `select "channels" { … }` is pinned by its HEAD name (`channels`),
        // not a case label — record a container-level select point keyed by the
        // head so a `list` run-set pin (`channels: [sms, email]`) resolves here.
        if (c.irKind === "select") {
          const sn = c.node as SelectNode;
          selects.set(c.nodeId, sn);
          const head = sn.head?.text?.trim();
          if (head) add(head, "select", { containerNodeId: c.nodeId });
        }
        // A plain `each` loop (LoopNode → `loop:N` nodeId; a retry is `retry:N`
        // and belongs to P1b) is a P1c count bound AND a coverage boundary
        // (0/1/many). Record EVERY loop — a `when` count pin (`settle: 3`)
        // resolves through the head label; a label-less loop (`each in items`,
        // `each { }`) still yields its coverage cells (keyed by the collection or a
        // bare `each`), so an unpinned loop never falsely reports 100% covered.
        if (c.irKind === "loop") {
          const head = (c.node as LoopNode).head;
          const label = head?.label?.text?.trim();
          // Only a real head LABEL is a pin key (a `count`/`sequence` pin resolves
          // through it). A label-less loop is `unaddressable` — recorded for its
          // coverage cells but kept out of `byKey`, so its display key (collection
          // or `each`) can never collide with a same-named select/branch/step.
          if (label) add(label, "loop", { containerNodeId: c.nodeId });
          else add(head?.collection?.text?.trim() || "each", "loop", { containerNodeId: c.nodeId, unaddressable: true });
        }
        // A labelled `do X as pay1` opens a test-namespace: its inner choice
        // keys and step refs prefix under `pay1` (nested → the dotted path).
        if (c.irKind === "splice") {
          const label = (c.node as SpliceNode).asName?.name;
          if (label) {
            spliceLabels.push(label);
            byLabel.set(scope(), c.nodeId);
          } else {
            spliceLabels.push(""); // unlabelled: no segment, balances the pop
          }
        }
        stack.push({ container: c, branchIndex: 0 });
      },
      close(c) {
        stack.pop();
        if (c.irKind === "splice") spliceLabels.pop();
      },
      group(g: EmitGroup) {
        const top = stack[stack.length - 1];
        if (!top) return; // the implicit root body group — not a choice point
        const c = top.container;
        const containerNodeId = c.nodeId;
        switch (c.irKind) {
          case "branch":
            if (g.role === "arm") add(g.label ?? "", "if", { containerNodeId, armLabel: g.label ?? "", groupNodeId: g.nodeId });
            else if (g.role === "else") add("else", "if", { containerNodeId, armLabel: "else", groupNodeId: g.nodeId });
            break;
          case "guard":
            if (g.role === "attempt") add("try", "try", { containerNodeId, armLabel: "try", groupNodeId: g.nodeId });
            else if (g.role === "catch") add(g.label ?? "catch", "try", { containerNodeId, armLabel: g.label ?? "catch", groupNodeId: g.nodeId });
            break;
          case "retry":
            // A `each N try {} catch {} else {}` retry: its arms are FAILURE-POINT
            // coverage cells (attempt-succeeds / catch / else-exhausted), NOT
            // if/try enumeration arms — kind `retry` keeps them out of `byContainer`
            // (the outcome is P1b `retryOutcomes`, not `branchSelection`).
            // `unaddressable`: a retry's outcome is P1b (`retryOutcomes` / a
            // `fails` step injection), not a pin key — keep these out of `byKey`
            // so `try` doesn't collide with a standalone `try` / silently swallow.
            if (g.role === "attempt") add("try", "retry", { containerNodeId, armLabel: "try", groupNodeId: g.nodeId, unaddressable: true });
            else if (g.role === "catch") add(g.label ?? "catch", "retry", { containerNodeId, armLabel: g.label ?? "catch", groupNodeId: g.nodeId, unaddressable: true });
            else if (g.role === "elseBody") add("else", "retry", { containerNodeId, armLabel: "else", groupNodeId: g.nodeId, unaddressable: true });
            break;
          case "select":
            if (g.role === "case" && g.label) add(g.label, "select", { containerNodeId, armLabel: g.label, groupNodeId: g.nodeId });
            break;
          case "parallel":
            if (g.role === "branch") {
              const bi = top.branchIndex++;
              const node = c.node as ParallelNode;
              const branches = node.body.form === "fanOut" ? [] : node.body.branches;
              const name = branches[bi]?.name?.name;
              if (name) add(name, "parallel", { containerNodeId, armLabel: `branch:${bi}`, groupNodeId: g.nodeId });
              // The desugared await-timer branch (§2.2) sits at index === branches.length.
              // Register ONE boundary point, keyed by the head text AND (if present)
              // the `as` alias; record the group so the artifact step above is skipped.
              if (node.awaitClause && bi === branches.length) {
                awaitTimerGroups.push(g.nodeId);
                add(node.awaitClause.head.text, "await", { containerNodeId, groupNodeId: g.nodeId });
                const alias = node.awaitClause.asName?.text?.trim();
                if (alias) add(alias, "await", { containerNodeId, groupNodeId: g.nodeId });
              }
            }
            break;
          default:
            break;
        }
      },
    },
  );

  const byKey = new Map<string, ChoicePoint[]>();
  const byContainer = new Map<string, ChoicePoint[]>();
  for (const p of points) {
    if (p.unaddressable) continue; // coverage-only point — never a pin key
    (byKey.get(p.key) ?? byKey.set(p.key, []).get(p.key)!).push(p);
    if (p.containerNodeId && (p.kind === "if" || p.kind === "try")) {
      (byContainer.get(p.containerNodeId) ?? byContainer.set(p.containerNodeId, []).get(p.containerNodeId)!).push(p);
    }
  }
  return { points, byKey, byContainer, byLabel, actionNodes, parallels, selects, branches, guardOfNode };
}

/** The arm-selector nodeId segments — mutually-exclusive alternatives of one
 *  container (an `if` arm, a `catch`, a select case, a parallel branch). */
const ARM_SEGMENT = /^(arm:\d+|else|catch:\d+|case:\d+|branch:\d+|attempt|elseBody)$/;

/** Do two step nodeIds sit in DIFFERENT arms of the same container — so no single
 *  execution runs both? (Then an `as` in one and a `go` in the other is a jump,
 *  not a loop.) They diverge into two arm-selector segments under a shared prefix;
 *  a divergence on any non-arm segment (or one being a prefix of the other) is
 *  same-flow. Mirrors the unroller, which only loops a `go` whose `as` really
 *  precedes it in the linearized trace. */
function mutuallyExclusive(a: string, b: string): boolean {
  const as = a.split("/");
  const bs = b.split("/");
  const n = Math.min(as.length, bs.length);
  for (let i = 0; i < n; i++) {
    if (as[i] !== bs[i]) return ARM_SEGMENT.test(as[i]!) && ARM_SEGMENT.test(bs[i]!);
  }
  return false; // one is a prefix of the other → same flow (ancestor predecessor)
}

/** The string key a pin resolves through: the dotted join of its segments —
 *  a bare `"declined"` at root, or a `do`-splice-namespaced `pay1."declined"`
 *  (design §6). A `caller >`-prefixed ref (an inbound-arrival pin) is deferred
 *  to P1b → undefined. */
function refKey(ref: TestRef): string | undefined {
  if (ref.caller) return undefined;
  if (ref.segments.length === 0) return undefined;
  return ref.segments.map((s) => s.value).join(".");
}

/** A pin value that DESELECTS the arm it names (`no` / `never`). Everything else
 *  — `yes`/`pass`/`ok`/`done`, a `fails` inject selecting a catch, a bare cause —
 *  is a positive selection of that arm. */
function isNegative(value: TestPinValue): boolean {
  return value.kind === "answer" && (value.word === "no" || value.word === "never");
}

interface PinnedArm {
  readonly armLabel: string;
  readonly positive: boolean;
}

/** Fold an `if` container's pinned arms into the chosen arm label. First
 *  positively-pinned arm wins (document order); all-negative falls to `else`
 *  when the container has one, else leaves the container at its default. */
function resolveIfSelection(pinned: readonly PinnedArm[], armLabels: readonly string[]): string | undefined {
  const positive = pinned.find((p) => p.positive);
  if (positive) return positive.armLabel;
  if (pinned.length > 0 && armLabels.includes("else")) return "else";
  return undefined;
}

/** Fold a `guard` container's pinned arms: a positively-pinned catch (or `try`)
 *  wins; otherwise default to `try`. */
function resolveGuardSelection(pinned: readonly PinnedArm[]): string | undefined {
  const positive = pinned.find((p) => p.positive);
  if (positive) return positive.armLabel;
  if (pinned.length > 0) return "try";
  return undefined;
}

const err = (message: string, code: DiagnosticCode, source?: TestPin["source"]): Diagnostic => ({
  severity: "error",
  message,
  code,
  ...(source ? { source } : {}),
});

/** A stable key for a produced trace — its event nodeId/kind sequence plus its
 *  terminal outcome. Two cartesian combinations that differ only in an
 *  UNREACHABLE nested choice produce identical traces → identical keys → one
 *  survives (correct cardinality: `if A { if B } else` is 3 traces, not 4). */
function traceKey(t: Trace): string {
  const evs = t.events
    .map((e) => (e.kind === "step" ? `s:${e.nodeId}` : `t:${e.nodeId}:${e.type}${e.all ? "!" : ""}`))
    .join("|");
  const end = t.end ? `${t.end.type}${t.end.all ? "!" : ""}:${t.end.reason ?? ""}` : "";
  return `${evs}#${end}`;
}

/** The full cartesian product of the per-container arm choices → one
 *  branchSelection map per combination. */
function cartesian(dims: readonly { readonly cid: string; readonly values: readonly string[] }[]): Map<string, string>[] {
  let combos: Map<string, string>[] = [new Map()];
  for (const d of dims) {
    const next: Map<string, string>[] = [];
    for (const c of combos) for (const v of d.values) {
      const m = new Map(c);
      m.set(d.cid, v);
      next.push(m);
    }
    combos = next;
  }
  return combos;
}

/** The cartesian product of the per-parallel race outcomes → one
 *  `parallelOutcomes` map per combination (P4). No parallel dims ⇒ one empty
 *  map (the pure if/try path). */
function parallelCombos(
  dims: readonly { readonly nodeId: NodeId; readonly outs: readonly ParallelOutcome[] }[],
): Map<NodeId, ParallelOutcome>[] {
  let combos: Map<NodeId, ParallelOutcome>[] = [new Map()];
  for (const d of dims) {
    const next: Map<NodeId, ParallelOutcome>[] = [];
    for (const c of combos) for (const o of d.outs) {
      const m = new Map(c);
      m.set(d.nodeId, o);
      next.push(m);
    }
    combos = next;
  }
  return combos;
}

/**
 * Resolve `when` pins into per-container if/try arm choices + loop/go bounds,
 * reusing the choice bridge. Shared by {@link enumerateTraces} and
 * {@link resolvePinnedBranches}; unmatched/ambiguous-pin diagnostics are pushed
 * into `diagnostics`. Timing (`after`/`takes`) and select/parallel/await pins are
 * not consumed here — they carry no if/try arm selection.
 */
function resolvePinConstraints(
  pins: readonly TestPin[],
  bridge: ChoiceBridge,
  diagnostics: Diagnostic[],
): {
  readonly pinnedValue: Map<string, string>;
  readonly loopCountByContainer: Map<string, number>;
  readonly goLenByTarget: Map<string, number>;
  readonly goExitBranch: Map<string, string>;
  readonly selectRunsByContainer: Map<string, readonly number[]>;
} {
  // Accumulate per-container pinned arms for the if/try fold.
  const pinnedByContainer = new Map<string, { kind: ChoiceKind; arms: PinnedArm[] }>();
  // P1c: pinned loop counts (loop containerNodeId → count) and pinned go-loop
  // lengths (go target name → count), which override the `maxLoop` default.
  const loopCountByContainer = new Map<string, number>();
  const goLenByTarget = new Map<string, number>();
  // P5 item 5: go targets whose branch-controlled sequence ends on a negative
  // answer → the branch container that gates the go. The final (exit) pass takes
  // the OTHER arm, so the unroller emits N gos + a final pass with the gating
  // branch's steps excluded (only the pre-branch checkpoint remains).
  const goExitBranch = new Map<string, string>();
  // P5: pinned `select` run-sets — a `list` pin on the select head resolves to
  // the ordered case indices the producer runs (its `selectRuns` Selection).
  const selectRunsByContainer = new Map<string, readonly number[]>();

  for (const pin of pins) {
    // Timing pins (`after`/`takes`) are P4 injection inputs, not choice-arm
    // selections — consumed by `buildInjectionMap` above. They legitimately name
    // plain calls / `Caller >`-prefixed inbound steps (not choice points), so
    // skip the choice-key resolution that would flag them as unmatched.
    if (pin.value.kind === "after" || pin.value.kind === "takes") continue;
    // A bare-string RHS (`kind: "cause"`) is only meaningful as the multi-await
    // `start: "<trigger>"` boundary form (consumed by resolveCompletions). Anywhere
    // else it matches no pin form — flag it, never silently ignore (parser stays
    // dumb; eval validates).
    if (pin.value.kind === "cause") {
      if (refKey(pin.key) !== "start") {
        diagnostics.push(err(`Unrecognized pin value (a bare string) for '${refKey(pin.key) ?? "?"}' — a pin value is an answer word, \`fails\`, \`after\`/\`takes\`, a count, or a \`[list]\`; a bare string is only valid as \`start: "<trigger>"\`.`, "test.unrecognized-pin", pin.source));
      }
      continue;
    }
    const key = refKey(pin.key);
    if (key === undefined) {
      diagnostics.push(err(`Inbound-arrival pin keys are not yet resolved (P1b).`, "test.unmatched-pin", pin.source));
      continue;
    }
    const pts = bridge.byKey.get(key) ?? [];
    if (pts.length === 0) {
      // A failure-injection pin (`fails` / a failure sequence) legitimately names
      // a STEP, not a choice point — it is resolved by `buildFailureMap`, which
      // reports its own unmatched diagnostic. A choice-point-keyed `fails` (a
      // catch label) still matches above and selects that arm (P1a).
      if (isFailurePin(pin.value)) continue;
      diagnostics.push(err(`No choice point matches pin key '${key}'.`, "test.unmatched-pin", pin.source));
      continue;
    }
    if (pts.length > 1) {
      diagnostics.push(err(`Pin key '${key}' is ambiguous (${pts.length} choice points) — disambiguate with an \`as\` label or a qualified path.`, "test.ambiguous-choice-key", pin.source));
      continue;
    }
    const pt = pts[0]!;
    if ((pt.kind === "if" || pt.kind === "try") && pt.containerNodeId && pt.armLabel) {
      const entry = pinnedByContainer.get(pt.containerNodeId) ?? { kind: pt.kind, arms: [] };
      entry.arms.push({ armLabel: pt.armLabel, positive: !isNegative(pin.value) });
      pinnedByContainer.set(pt.containerNodeId, entry);
      // Item 5 — an answer pin on an `if` arm that gates a backward `go` bounds
      // that go: a sequence (`"retry?": yes, yes, no`) → leading positives are the
      // loop count, a trailing negative is the exit pass; a scalar `no`/`never` →
      // exit immediately (0 re-entries). The arm push above already selects the
      // gated arm for the pre-unroll trace.
      if (pt.kind === "if") {
        const spec = goLoopFromPin(pin.value);
        if (spec) {
          const bn = bridge.branches.get(pt.containerNodeId);
          const goTarget = bn ? findGoTargetInBranch(bn) : undefined;
          if (goTarget !== undefined) {
            goLenByTarget.set(goTarget, spec.gos);
            if (spec.exits) goExitBranch.set(goTarget, pt.containerNodeId);
          }
        }
      }
    } else if (pt.kind === "loop" && pt.containerNodeId) {
      // A `count`/`sequence` pin bounds the loop to that count (no OPEN).
      const n = pinCount(pin.value);
      if (n !== undefined) loopCountByContainer.set(pt.containerNodeId, n);
    } else if (pt.kind === "as") {
      // A `count`/`sequence` pin on an `as` step bounds a `go`-loop that
      // re-enters it (consumed only if a backward `go` targets the alias). The
      // producer matches the RAW alias (`checkpoint`), not the namespaced key.
      const n = pinCount(pin.value);
      if (n !== undefined) goLenByTarget.set(pt.raw, n);
    } else if (pt.kind === "select" && pt.containerNodeId && pin.value.kind === "list") {
      // A `list` run-set pin (`channels: [sms, email]`) → the ordered case
      // indices to run, clamped by the select's `one`/`N`/`all` count (P5).
      const runs = resolveSelectRun(pin.value, bridge.selects.get(pt.containerNodeId));
      if (runs) selectRunsByContainer.set(pt.containerNodeId, runs);
    }
    // parallel/await completion pins are valid but not consumed here — resolved
    // separately by the per-branch completion + await-interrupt passes (P5).
  }

  // A pinned container collapses to its single chosen arm (an all-negative pin
  // with no `else` falls back to the container's first arm — the pin intent is
  // "constrained here," so it stays 1, not re-enumerated).
  const pinnedValue = new Map<string, string>();
  for (const [containerNodeId, { kind, arms }] of pinnedByContainer) {
    const armLabels = (bridge.byContainer.get(containerNodeId) ?? []).map((p) => p.armLabel ?? "");
    const chosen = (kind === "if" ? resolveIfSelection(arms, armLabels) : resolveGuardSelection(arms)) ?? armLabels[0];
    if (chosen !== undefined) pinnedValue.set(containerNodeId, chosen);
  }
  return { pinnedValue, loopCountByContainer, goLenByTarget, goExitBranch, selectRunsByContainer };
}

/** Is a pin value a comma sequence of PURE answer words (`yes, yes, no`) — the
 *  branch-controlled go-loop form, distinct from a per-attempt failure sequence
 *  (which carries a `fails` atom)? */
function isAnswerSequence(v: TestPinValue): boolean {
  return v.kind === "sequence" && v.steps.length > 0 && v.steps.every((s) => s.kind === "answer");
}

/** Read a branch-controlled go loop off an answer sequence: `gos` = the leading
 *  positive answers (iterations that took the go); `exits` = a trailing negative
 *  answer terminated it (the final pass leaves the loop). */
function goLoopFromSequence(v: TestPinValue): { gos: number; exits: boolean } {
  if (v.kind !== "sequence") return { gos: 0, exits: false };
  let gos = 0;
  for (const s of v.steps) {
    if (s.kind === "answer" && (s.word === "no" || s.word === "never")) return { gos, exits: true };
    gos++;
  }
  return { gos, exits: false }; // all positive → no pinned exit
}

/** A branch-controlled go loop bound from an answer pin: a comma sequence
 *  (`yes, yes, no`) via {@link goLoopFromSequence}, OR a SCALAR `no`/`never`
 *  (`"retry?": no`) = a leading-no with empty tail = exit immediately (0
 *  re-entries). A scalar affirmative is not a bounded loop (undefined). */
function goLoopFromPin(v: TestPinValue): { gos: number; exits: boolean } | undefined {
  if (isAnswerSequence(v)) return goLoopFromSequence(v);
  if (v.kind === "answer" && (v.word === "no" || v.word === "never")) return { gos: 0, exits: true };
  return undefined;
}

/** The `go` target a branch controls: the first `go <name>` re-entry directly in
 *  one of its arm/else bodies (the loop-gating go sits in the branch body). */
function findGoTargetInBranch(bn: BranchNode): string | undefined {
  const scan = (flow: Flow | undefined): string | undefined => {
    for (const n of flow ?? []) {
      if (n.kind === "reentry") return (n as ReentryNode).target.name;
    }
    return undefined;
  };
  for (const arm of bn.arms as readonly BranchArm[]) {
    const t = scan(arm.body);
    if (t !== undefined) return t;
  }
  return scan(bn.elseArm?.body);
}

/** Resolve a `select` `list` pin into the ordered case indices to run: each
 *  listed item mapped to its case index (in listed order, unknown labels
 *  dropped), then clamped by the select's `one` (first) / `N` (first N) / `all`
 *  count. An empty list ⇒ `[]` (no case runs). */
function resolveSelectRun(value: TestPinValue, sn: SelectNode | undefined): readonly number[] | undefined {
  if (!sn || value.kind !== "list") return undefined;
  const labels = sn.cases.map((c) => c.label.text.trim());
  const wanted: number[] = [];
  for (const item of value.items) {
    const idx = labels.indexOf(item.trim());
    if (idx >= 0) wanted.push(idx);
  }
  const n = sn.count.kind === "all" ? wanted.length : sn.count.kind === "one" ? 1 : sn.count.n;
  return wanted.slice(0, Math.max(0, n));
}

/**
 * The if/try branch selections a test's `when` pins resolve to — the
 * `WalkerState.branchSelection` map (containerNodeId → armLabel) that replays a
 * test's answered trace in the scrubber (P7 panel). Canonical: the SAME
 * resolution {@link enumerateTraces} uses, so a positive pin selects its arm and
 * a negative (`no`/`never`) pin falls to the `else`/other arm — no divergent
 * re-derivation on the UI side. `select`/`parallel`/`await` pins carry no branch
 * selection and are absent from the map.
 */
export function resolvePinnedBranches(
  process: ProcessModel,
  pins: readonly TestPin[],
  ctx: WalkerContext,
): ReadonlyMap<string, string> {
  const bridge = buildChoiceBridge(process, ctx);
  return resolvePinConstraints(pins, bridge, []).pinnedValue;
}

/**
 * Materialise the pin-constrained trace-SET. Resolves the `if`/`try` pins into a
 * per-container chosen arm, enumerates every UNPINNED `if`/`try` container over
 * its arms (cartesian product), drives {@link produceTrace} per combination, and
 * deduplicates. `select`/`parallel` are produced at their structural default and
 * recorded as `open` (their outcome derivation is P4/P5).
 */
export function enumerateTraces(
  process: ProcessModel,
  pins: readonly TestPin[],
  ctx: WalkerContext,
  opts: EnumerateOptions = {},
): EnumerateResult {
  const diagnostics: Diagnostic[] = [];
  const maxLoop = opts.maxLoop ?? DEFAULT_MAX_LOOP;
  const traceCap = opts.traceCap ?? DEFAULT_TRACE_CAP;
  const env: LatencyEnv = opts.model
    ? buildLatencyEnv(opts.model, process)
    : { latencyOf: () => ({ kind: "missing" }), subprocessByName: ctx.subprocessByName, processByName: ctx.processByName };

  if (process.id == null && (process.name == null || process.name === "")) {
    diagnostics.push(err("Process under test has no name — cannot enumerate traces.", "test.untestable-process"));
    return { traces: [], open: [], openBounds: [], spliceLabels: new Map(), diagnostics, env, inject: new Map(), coverageCells: new Set() };
  }

  const bridge = buildChoiceBridge(process, ctx);
  // P4: the pin-injection map (after/takes → IR node) + the per-parallel race
  // outcomes it induces. Deterministic winners resolve their container (dropped
  // from `open`); a nondeterministic race yields every plausible outcome.
  const inject = buildInjectionMap(bridge.actionNodes, pins);
  const race = resolveRaces(process, bridge.parallels, inject, env);
  // P5 items 2-3: EXPLICIT per-branch completion + `elapses`/`within` await
  // interrupt pins override the timing-derived race outcome for their container.
  const completions = resolveCompletions(bridge.parallels, pins, diagnostics);
  const resolvedParallels = new Set<string>([...race.resolved, ...completions.keys()]);

  // P3: the failure-injection map (`fails` / failure-sequence pin → IR node).
  // Combination-invariant, so build once and feed into every produced trace. A
  // `fails` whose key matches a CHOICE POINT (a catch label) is arm-selection
  // (P1a), NOT a step injection — exclude those so `buildFailureMap` doesn't
  // flag them unmatched.
  const stepFailPins = pins.filter(
    (p) => isFailurePin(p.value) && (bridge.byKey.get(refKey(p.key) ?? "") ?? []).length === 0,
  );
  const failure = buildFailureMap(bridge.actionNodes, stepFailPins);
  for (const pin of failure.unmatched) {
    diagnostics.push(err(`No step matches failure pin '${refKey(pin.key) ?? "?"}'.`, "test.unmatched-fail", pin.source));
  }

  // Resolve `when` pins into per-container if/try arm choices + loop/go bounds.
  const { pinnedValue, loopCountByContainer, goLenByTarget, goExitBranch, selectRunsByContainer } = resolvePinConstraints(pins, bridge, diagnostics);

  // A guard whose attempt carries an injected failure has a fixed outcome (run
  // the attempt, which fails and routes to a catch inside produceTrace) — pin it
  // to `try` so the enumerator does NOT also emit its catch arm as a rival trace.
  for (const node of failure.map.keys()) {
    for (const gid of bridge.guardOfNode.get(node) ?? []) {
      if (!pinnedValue.has(gid)) pinnedValue.set(gid, "try");
    }
  }

  // Enumeration dimensions: one per selectable `if`/`try` container. Pinned ⇒ a
  // single value; unpinned ⇒ all arms (document order preserved from the bridge).
  const dims = [...bridge.byContainer].map(([cid, pts]) => {
    const pinned = pinnedValue.get(cid);
    return { cid, values: pinned !== undefined ? [pinned] : pts.map((p) => p.armLabel ?? "") };
  });

  // P1c loop bounds — static (numeric head) or pin-bounded, independent of which
  // if/try arm runs, so resolve them ONCE from a full-branch probe walk and feed
  // the counts into every produced trace (produceTrace unrolls loops itself).
  // produceTrace ALWAYS inlines `do` splices, so the probe must too — expand every
  // splice to a fixpoint (deeper `do`s surface as each level expands), else a loop
  // reached only through a `do` is unrolled with no bound and no OPEN.
  const openBounds: OpenBound[] = [];
  const expanded = new Set<string>();
  let probeTree: readonly WalkNode[] = [];
  for (;;) {
    const probe = walkProcess(
      process,
      { branchSelection: new Map(), expandedSubprocesses: expanded },
      ctx,
      { showAllBranches: true },
    );
    probeTree = probe.tree;
    const before = expanded.size;
    collectDoIds(probeTree, expanded);
    if (expanded.size === before) break;
  }
  const bounds = loopBounds(probeTree, loopCountByContainer, maxLoop, openBounds);
  const loopCounts = new Map<string, number>();
  for (const [nodeId, b] of bounds) loopCounts.set(nodeId, b.count);

  // P1b × P1c × P4: cartesian if/try selection × per-parallel race outcomes →
  // produceTrace (loops unrolled via loopCounts) → backward-`go` unroll → dedup.
  const parDimsMap = new Map<NodeId, readonly ParallelOutcome[]>(race.candidates);
  for (const [nodeId, o] of completions) parDimsMap.set(nodeId, [o]); // explicit overrides timing
  const parDims = [...parDimsMap].map(([nodeId, outs]) => ({ nodeId, outs }));
  const seen = new Map<string, Trace>();
  // Per backward `go`-loop, the re-entry count the unroller resolved (target →
  // count) — the exercised loop-boundary bucket for coverage (§11 item 6).
  const goLoopCounts = new Map<string, number>();
  for (const branchSelection of cartesian(dims)) {
    for (const parallelOutcomes of parallelCombos(parDims)) {
      const produced = produceTrace(process, { branchSelection, loopCounts, parallelOutcomes, selectRuns: selectRunsByContainer, failures: failure.map }, ctx);
      const trace = unrollGos(produced, goLenByTarget, goExitBranch, maxLoop, openBounds, goLoopCounts);
      const key = traceKey(trace);
      if (!seen.has(key)) seen.set(key, trace);
    }
  }

  // `select`/`parallel` points ride at their structural default — recorded as
  // the deferred (P5) work list, keyed by container. A parallel whose race was
  // DETERMINISTICALLY resolved (P4) is dropped: its single outcome is in T.
  const openByContainer = new Map<string, { kind: ChoiceKind; armLabels: string[] }>();
  for (const p of bridge.points) {
    if ((p.kind === "select" || p.kind === "parallel") && p.containerNodeId) {
      // A select pinned to a run-set is fully resolved — drop it from OPEN so a
      // □ assertion over the pinned trace isn't falsely reported unprovable.
      if (p.kind === "select" && selectRunsByContainer.has(p.containerNodeId)) continue;
      if (p.kind === "parallel" && resolvedParallels.has(p.containerNodeId)) continue;
      const e = openByContainer.get(p.containerNodeId) ?? { kind: p.kind, armLabels: [] };
      if (p.armLabel) e.armLabels.push(p.armLabel);
      openByContainer.set(p.containerNodeId, e);
    }
  }
  const open: OpenChoice[] = [...openByContainer].map(([containerNodeId, e]) => ({
    containerNodeId,
    kind: e.kind,
    armLabels: e.armLabels,
  }));

  const traces = applyCap([...seen.values()], traceCap, openBounds, diagnostics);

  // P5 §11 — the choice-space cells this trace-set exercised. Arm/case/branch
  // cells are the bridge group nodeIds any surviving step falls under (a step's
  // nodeId is prefixed by its enclosing arm group); loop-bucket cells come from
  // the resolved loop counts; a `merge-fail` cell from a synthesized merge-fail
  // terminator. Coverage = these ∪-ed across a process's tests vs the space.
  const coverageCells = new Set<string>();
  for (const p of bridge.points) {
    if (p.groupNodeId && (p.kind === "if" || p.kind === "try" || p.kind === "select" || p.kind === "parallel" || p.kind === "retry")) {
      // A step under this group has nodeId `<group>/…` — the trailing `/` guard
      // stops `arm:1` from matching a step under `arm:10`/`arm:11`.
      const prefix = `${p.groupNodeId}/`;
      if (traces.some((t) => t.steps.some((s) => s.nodeId === p.groupNodeId || s.nodeId.startsWith(prefix)))) coverageCells.add(p.groupNodeId);
    }
  }
  for (const [nodeId, count] of loopCounts) coverageCells.add(`${nodeId}#${loopBucket(count)}`);
  // `go`-loop boundary buckets (0/1/many re-entries), keyed by target alias to
  // match the bridge's synthetic `goloop:<target>` container.
  for (const [target, gos] of goLoopCounts) coverageCells.add(`goloop:${target}#${loopBucket(gos)}`);
  for (const t of traces) {
    for (const e of t.events) {
      if (e.kind === "terminator" && e.nodeId.endsWith("/merge-fail")) {
        coverageCells.add(`${e.nodeId.slice(0, -"/merge-fail".length)}#merge-fail`);
      }
    }
  }

  return { traces, open, openBounds: dedupeOpenBounds(openBounds), spliceLabels: bridge.byLabel, diagnostics, env, inject, coverageCells };
}

/** A resolved per-loop-container bound: how many times to repeat its body. */
interface LoopBound {
  readonly count: number;
}

/** A numeric loop head (`each 3`) → the count; else null (collection / phrase /
 *  no head). Mirrors `process-latency.ts` `numericHead` — a loop's static count
 *  is the same fact whether it drives a latency estimate or a test trace. */
function numericHead(label: string | undefined, source: string | undefined): number | null {
  if (label === undefined || source !== undefined) return null;
  const t = label.trim();
  return /^\d+$/.test(t) ? Number.parseInt(t, 10) : null;
}

/** Extract a bound count from a `count` / `sequence` pin value. */
function pinCount(value: TestPinValue): number | undefined {
  if (value.kind === "count") return value.n;
  if (value.kind === "sequence") return value.steps.length;
  return undefined;
}

/**
 * Resolve every plain `each` loop container (walk-tree `each` node whose nodeId
 * ends in `loop:N`; a `retry:N` node is P1b's) to a repeat count: a pinned count
 * wins, else a numeric head, else `maxLoop` with an `OPEN(unbounded-each)` bound
 * recorded once per container.
 */
function loopBounds(
  tree: readonly WalkNode[],
  pinned: ReadonlyMap<string, number>,
  maxLoop: number,
  openBounds: OpenBound[],
): Map<string, LoopBound> {
  const bounds = new Map<string, LoopBound>();
  const visit = (nodes: readonly WalkNode[]): void => {
    for (const n of nodes) {
      switch (n.kind) {
        case "each":
          if (/\/loop:\d+$/.test(n.nodeId)) {
            const pin = pinned.get(n.nodeId);
            const num = numericHead(n.label, n.source);
            if (pin !== undefined) bounds.set(n.nodeId, { count: pin });
            else if (num !== null) bounds.set(n.nodeId, { count: num });
            else {
              bounds.set(n.nodeId, { count: maxLoop });
              openBounds.push({ reason: "unbounded-each", nodeId: n.nodeId, boundedTo: maxLoop });
            }
          }
          visit(n.children);
          break;
        case "if":
          n.branches.forEach((b) => visit(b.children));
          if (n.elseBranch) visit(n.elseBranch.children);
          break;
        case "switch":
          n.cases.forEach((b) => visit(b.children));
          break;
        case "try":
          visit(n.tryBranch.children);
          n.catches.forEach((b) => visit(b.children));
          break;
        case "parallel":
          n.branches.forEach((b) => visit(b.children));
          break;
        case "do":
          if (n.body) visit(n.body);
          break;
        default:
          break;
      }
    }
  };
  visit(tree);
  return bounds;
}

/** Collect every `do`-splice container nodeId in the tree (into `acc`) so the
 *  probe walk can expand them — including ones only revealed after a shallower
 *  splice was expanded. Recurses the same container children as {@link loopBounds}. */
function collectDoIds(nodes: readonly WalkNode[], acc: Set<string>): void {
  for (const n of nodes) {
    switch (n.kind) {
      case "do":
        acc.add(n.nodeId);
        if (n.body) collectDoIds(n.body, acc);
        break;
      case "each":
        collectDoIds(n.children, acc);
        break;
      case "if":
        n.branches.forEach((b) => collectDoIds(b.children, acc));
        if (n.elseBranch) collectDoIds(n.elseBranch.children, acc);
        break;
      case "switch":
        n.cases.forEach((b) => collectDoIds(b.children, acc));
        break;
      case "try":
        collectDoIds(n.tryBranch.children, acc);
        n.catches.forEach((b) => collectDoIds(b.children, acc));
        break;
      case "parallel":
        n.branches.forEach((b) => collectDoIds(b.children, acc));
        break;
      default:
        break;
    }
  }
}

/**
 * Unroll backward `go` re-entries in a produced trace: a `go <alias>` that
 * resumes at an earlier `as <alias>` step repeats the `[as … go]` span. A pinned
 * go-loop length bounds it exactly; otherwise it is bounded to `maxLoop` with an
 * `OPEN("go")` recorded (a `go` is a structural unbounded, per the latency-fold
 * vocabulary). Forward `go`s (target resolves later) are not loops and pass
 * through. Operates on the trace's ordered event stream (steps + terminators) so
 * `steps`, `events`, and `end` stay consistent. (Loops are already unrolled
 * inside {@link produceTrace} via `loopCounts` — this is the `go`-only pass.)
 */
function unrollGos(
  trace: Trace,
  pinnedLen: ReadonlyMap<string, number>,
  exitBranch: ReadonlyMap<string, string>,
  maxLoop: number,
  openBounds: OpenBound[],
  goLoopCounts?: Map<string, number>,
): Trace {
  const events = trace.events;
  const hasGo = events.some((e) => e.kind === "step" && (e as WalkStep).reentryTarget);
  if (!hasGo) return trace;
  const out: TraceEvent[] = [];
  let i = 0;
  while (i < events.length) {
    const e = events[i]!;
    const target = e.kind === "step" ? (e as WalkStep).reentryTarget : undefined;
    if (target !== undefined) {
      const asIdx = findAsIndexEvents(events, i, target);
      if (asIdx !== -1) {
        const span = events.slice(asIdx, i + 1);
        // `out` already holds this pass up to the step before the `go`.
        const pinned = pinnedLen.get(target);
        const container = exitBranch.get(target);
        if (container !== undefined) {
          // Branch-controlled loop (item 5): `gos` full `[as … go]` passes, then
          // an exit pass that takes the OTHER arm — so the gating branch's steps
          // (arm interior INCLUDING the `go`) must not appear; only the pre-branch
          // checkpoint remains. Drop the partial pass already streamed into `out`
          // (the shallow gating go has no nested go in its span), then rebuild.
          const gos = pinned ?? maxLoop;
          goLoopCounts?.set(target, gos); // re-entries (item 5: 0 for a leading `no`)
          const preBranch = span.filter((ev) => !ev.nodeId.startsWith(`${container}/`));
          out.length -= i - asIdx; // remove events[asIdx .. i-1] streamed so far
          for (let k = 0; k < gos; k++) out.push(...span);
          out.push(...preBranch); // final exit pass — gating branch excluded
          i++;
          continue;
        }
        {
          // Uniform go loop: repeat the whole `[as … go]` span `count` times.
          const count = pinned ?? maxLoop;
          goLoopCounts?.set(target, count); // re-entries
          out.push(e);
          for (let k = 1; k < count; k++) out.push(...span);
          if (pinned === undefined) openBounds.push({ reason: "go", nodeId: (e as WalkStep).nodeId, boundedTo: maxLoop });
        }
        i++;
        continue;
      }
    }
    out.push(e);
    i++;
  }
  const steps = out.filter((x): x is WalkStep => x.kind === "step");
  // Re-derive stepNodes aligned to the unrolled steps: the repeated span reuses
  // the same WalkStep references, so a WalkStep→node map recovers each node
  // (the same node repeated across occurrences — the correct pruned-fold input).
  const nodeOf = new Map<WalkStep, ProcessNode>();
  trace.steps.forEach((s, i) => nodeOf.set(s, trace.stepNodes[i]!));
  const stepNodes = steps.map((s) => nodeOf.get(s)!);
  return { ...trace, events: out, steps, stepNodes };
}

/** Index of the nearest preceding `as <target>` step event (a backward `go`'s
 *  anchor), or -1 when the alias is only defined later (a forward `go`). */
function findAsIndexEvents(events: readonly TraceEvent[], goIdx: number, target: string): number {
  for (let k = goIdx - 1; k >= 0; k--) {
    const e = events[k]!;
    if (e.kind === "step" && (e as WalkStep).asName === target) return k;
  }
  return -1;
}

/**
 * Cap the trace-set cardinality. Overflow is OPEN, never a silent drop: the
 * first `cap` traces are kept, an `OPEN("cap")` bound and a `warning` diagnostic
 * are recorded so the caller reports the set as incomplete.
 */
function applyCap(
  traces: readonly Trace[],
  cap: number,
  openBounds: OpenBound[],
  diagnostics: Diagnostic[],
): Trace[] {
  if (traces.length <= cap) return [...traces];
  openBounds.push({ reason: "cap" });
  diagnostics.push({
    severity: "warning",
    message: `Trace-set exceeded the enumeration cap (${cap}); the set is OPEN — showing the first ${cap} of ${traces.length} traces.`,
    code: "test.trace-cap",
  });
  return traces.slice(0, cap);
}

/** Collapse duplicate OPEN bounds — the same loop/`go`/cap bound surfaces once
 *  per cartesian trace during `go` unrolling; the caller wants it reported once. */
function dedupeOpenBounds(bounds: readonly OpenBound[]): OpenBound[] {
  const seen = new Set<string>();
  const out: OpenBound[] = [];
  for (const b of bounds) {
    const key = `${b.reason}|${b.nodeId ?? ""}|${b.boundedTo ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(b);
  }
  return out;
}
